import Image from 'next/image';
import { oneLine } from 'common-tags';

import { Container, RichInline } from '@/atoms';
import { Paso } from '@/molecules';

import {
  blockSpacing,
  eyebrowClass,
  sectionBgProps,
  textStyleClass,
} from '@/utils';
import { getRealUrl } from '@/utils/utils';

/**
 * Header (eyebrow + título + subtítulo) + grid 1/2/4 de `Paso` (icono + título
 * + descripción). Pensado para secciones tipo "¿Cómo funciona el servicio?" —
 * sin caja, sin sombra, items planos alineados a la izquierda.
 */
export const ComponentRevisadasPasos = ({
  Eyebrow,
  Titulo,
  Subtitulo,
  EyebrowEstilo,
  TituloEstilo,
  SubtituloEstilo,
  PasosTituloEstilo,
  PasosDescripcionEstilo,
  PieTexto,
  PieTextoEstilo,
  Pasos,
  ConectorIcono,
  Ancla,
  FondoSeccion,
  Espaciado,
}: any) => {
  if (!Pasos?.length) return null;
  // Conector ">>>" opcional entre pasos (Figma 2182:6656). Sólo se pinta en
  // lg+ (donde los pasos van en fila) y nunca tras el último. Si el tenant no
  // sube asset, no se renderiza nada.
  const conectorUrl = ConectorIcono?.url
    ? getRealUrl(ConectorIcono.url)
    : undefined;
  const eyebrowCls = eyebrowClass(EyebrowEstilo);
  const tituloCls = textStyleClass(TituloEstilo);
  const subtituloCls = textStyleClass(SubtituloEstilo);
  // Defaults a nivel de bloque — pick per-paso gana.
  const pasosTituloCls = textStyleClass(PasosTituloEstilo);
  const pasosDescripcionCls = textStyleClass(PasosDescripcionEstilo);
  const pieTextoCls = textStyleClass(PieTextoEstilo);

  return (
    <Container
      wrapper
      classNameWrapper={blockSpacing(Espaciado)}
      id={Ancla}
      {...sectionBgProps(FondoSeccion)}
    >
      {(Eyebrow || Titulo || Subtitulo) && (
        <div className="mb-12 flex max-w-full flex-col gap-2">
          {(Eyebrow || Titulo) && (
            <div className="flex flex-col items-end self-start">
              {Eyebrow && (
                <RichInline
                  html={Eyebrow}
                  className={oneLine`font-accent text-primary-500 ${
                    eyebrowCls ?? 'text-3xl leading-10 font-normal'
                  }`}
                />
              )}
              {Titulo && (
                <RichInline
                  as="h2"
                  html={Titulo}
                  className={oneLine`text-headline text-text-primary font-primary ${
                    tituloCls ?? 'text-headline-h1 font-bold'
                  }`}
                />
              )}
            </div>
          )}
          {Subtitulo && (
            <RichInline
              as="p"
              html={Subtitulo}
              className={oneLine`text-body text-text-primary ${
                subtituloCls ?? 'text-body-l'
              }`}
            />
          )}
        </div>
      )}

      <ul className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-20">
        {Pasos.map((paso: any, index: number) => (
          <li key={paso.id ?? index} className="relative h-full">
            <Paso
              icon={
                paso.Icono
                  ? {
                      src: getRealUrl(paso.Icono.url),
                      alt: paso.Icono.alternativeText ?? paso.Titulo,
                    }
                  : undefined
              }
              number={index + 1}
              title={paso.Titulo}
              description={paso.Descripcion}
              titleStyleClass={
                textStyleClass(paso.TituloEstilo) ?? pasosTituloCls
              }
              descriptionStyleClass={
                textStyleClass(paso.DescripcionEstilo) ?? pasosDescripcionCls
              }
            />
            {conectorUrl && index < Pasos.length - 1 && (
              <span
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-full hidden w-20 -translate-y-1/2 lg:flex lg:items-center lg:justify-center"
              >
                <Image
                  src={conectorUrl}
                  alt=""
                  width={32}
                  height={21}
                  className="object-contain"
                />
              </span>
            )}
          </li>
        ))}
      </ul>

      {PieTexto && (
        <RichInline
          as="p"
          html={PieTexto}
          className={oneLine`text-headline text-text-primary font-primary mt-12 text-center ${
            pieTextoCls ?? 'text-[24px] leading-8 font-bold'
          }`}
        />
      )}
    </Container>
  );
};

ComponentRevisadasPasos.displayName = 'ComponentRevisadasPasos';
